import { A4_LOGICAL_WIDTH } from './answers';

// The ink page's logical width (A4_LOGICAL_WIDTH) maps to A4 portrait = 210 mm = 21 cm, so this
// is how many logical units make one centimetre. It lets shape tools report real-world sizes
// (length / radius / diameter) that hold on any device and on the printed/graded sheet.
export const LOGICAL_PER_CM = A4_LOGICAL_WIDTH / 21;

/** The measured shapes a student can draw on the ink canvas. */
export type ShapeKind = 'line' | 'arrow' | 'rect' | 'circle' | 'ellipse' | 'arc';

/** Toolbar entries (order shown top→bottom in the dropdown), each with a compact glyph. */
export const SHAPE_TOOLS: { kind: ShapeKind; label: string; icon: string }[] = [
  { kind: 'line', label: 'Line', icon: '╱' },      // ╱
  { kind: 'arrow', label: 'Arrow', icon: '↗' },    // ↗
  { kind: 'rect', label: 'Rectangle', icon: '▭' }, // ▭
  { kind: 'circle', label: 'Circle', icon: '◯' },  // ◯
  { kind: 'ellipse', label: 'Ellipse', icon: '⬭' },// ⬭
  { kind: 'arc', label: 'Arc', icon: '⌒' },        // ⌒
];

export const SHAPE_LABELS: Record<ShapeKind, string> =
  Object.fromEntries(SHAPE_TOOLS.map(s => [s.kind, s.label])) as Record<ShapeKind, string>;

interface Pt { x: number; y: number; }

export interface ShapeGeometry {
  /** Polyline approximating the shape, in logical page coordinates. */
  points: Pt[];
  /** Live measurement readout, e.g. "5.3 cm" or "r 2.5 · ⌀ 5.0 cm". */
  label: string;
  /** Where to anchor the readout pill (logical coordinates). */
  labelAt: Pt;
}

const toCm = (dLogical: number) => dLogical / LOGICAL_PER_CM;
const fmt = (cm: number) => cm.toFixed(1);
const dist = (a: Pt, b: Pt) => Math.hypot(b.x - a.x, b.y - a.y);
/** Keep the readout clear of the cursor/shape edge. */
const LABEL_GAP = 14;

/**
 * Build the polyline + measurement for a shape dragged from `a` (press) to `c` (current).
 * Circle/ellipse treat `a` as the centre; line/arrow/rect treat `a`→`c` as the two anchors.
 */
export function shapeGeometry(kind: ShapeKind, a: Pt, c: Pt): ShapeGeometry {
  switch (kind) {
    case 'line': {
      const len = dist(a, c);
      return {
        points: [a, c],
        label: `${fmt(toCm(len))} cm`,
        labelAt: { x: (a.x + c.x) / 2, y: (a.y + c.y) / 2 - LABEL_GAP },
      };
    }
    case 'arrow': {
      const len = dist(a, c);
      const ang = Math.atan2(c.y - a.y, c.x - a.x);
      const head = Math.min(20, len * 0.32);
      const spread = Math.PI / 7;
      const b1 = { x: c.x - head * Math.cos(ang - spread), y: c.y - head * Math.sin(ang - spread) };
      const b2 = { x: c.x - head * Math.cos(ang + spread), y: c.y - head * Math.sin(ang + spread) };
      // Retrace the tip so the arrowhead draws as one continuous polyline.
      return {
        points: [a, c, b1, c, b2],
        label: `${fmt(toCm(len))} cm`,
        labelAt: { x: (a.x + c.x) / 2, y: (a.y + c.y) / 2 - LABEL_GAP },
      };
    }
    case 'rect': {
      const { x: x0, y: y0 } = a;
      const { x: x1, y: y1 } = c;
      const w = Math.abs(x1 - x0), h = Math.abs(y1 - y0);
      return {
        points: [
          { x: x0, y: y0 }, { x: x1, y: y0 }, { x: x1, y: y1 }, { x: x0, y: y1 }, { x: x0, y: y0 },
        ],
        label: `${fmt(toCm(w))} × ${fmt(toCm(h))} cm`,
        labelAt: { x: (x0 + x1) / 2, y: Math.min(y0, y1) - LABEL_GAP },
      };
    }
    case 'circle': {
      const r = dist(a, c);
      const pts: Pt[] = [];
      const N = 72;
      for (let i = 0; i <= N; i++) {
        const t = (i / N) * Math.PI * 2;
        pts.push({ x: a.x + r * Math.cos(t), y: a.y + r * Math.sin(t) });
      }
      return {
        points: pts,
        label: `r ${fmt(toCm(r))} · ⌀ ${fmt(toCm(r * 2))} cm`,
        labelAt: { x: a.x, y: a.y - r - LABEL_GAP },
      };
    }
    case 'ellipse': {
      const rx = Math.abs(c.x - a.x), ry = Math.abs(c.y - a.y);
      const pts: Pt[] = [];
      const N = 84;
      for (let i = 0; i <= N; i++) {
        const t = (i / N) * Math.PI * 2;
        pts.push({ x: a.x + rx * Math.cos(t), y: a.y + ry * Math.sin(t) });
      }
      return {
        points: pts,
        label: `${fmt(toCm(rx * 2))} × ${fmt(toCm(ry * 2))} cm`,
        labelAt: { x: a.x, y: a.y - ry - LABEL_GAP },
      };
    }
    case 'arc': {
      // Centre at the press point; the drag sets the radius and the end angle. The arc sweeps
      // from the horizontal (0°) to the drag direction, so the readout shows radius + angle.
      const r = dist(a, c);
      const end = Math.atan2(c.y - a.y, c.x - a.x);        // signed, from the +x axis
      const steps = Math.max(2, Math.round(Math.abs(end) / (Math.PI / 60))); // ~3° per segment
      const pts: Pt[] = [];
      for (let i = 0; i <= steps; i++) {
        const t = end * (i / steps);
        pts.push({ x: a.x + r * Math.cos(t), y: a.y + r * Math.sin(t) });
      }
      const deg = Math.round((Math.abs(end) * 180) / Math.PI);
      const mid = end / 2;
      return {
        points: pts,
        label: `r ${fmt(toCm(r))} cm · ${deg}°`,
        labelAt: { x: a.x + (r + LABEL_GAP) * Math.cos(mid), y: a.y + (r + LABEL_GAP) * Math.sin(mid) },
      };
    }
  }
}
